import { useState } from "react";
import { Gift, Minus, Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ConfirmModal } from "../../../../components/modal/confirm-modal";
import { Button } from "../../../../components/button";
import { Input } from "../../../../components/input";

export interface EGiftCardQuantityItem {
  id: string | number;
  title: string;
  description?: string;
  value: string | number;
  usedQuantity?: number;
  [key: string]: unknown;
  disableIncrease?: boolean;
  disableDecrease?: boolean;
  minValue?: number;
  allowRemove?: boolean;
}

interface EGiftCardQuantityEditorProps {
  item: EGiftCardQuantityItem;
  handleChangeValue: (id: string | number, value: number) => void;
  handleRemoveValue: (value: string | number) => void;
}

interface PendingRemoval {
  id: string | number;
}

function EGiftCardQuantityEditor({
  item,
  handleChangeValue,
  handleRemoveValue,
}: EGiftCardQuantityEditorProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState(item.value);
  const [showConfirmClear, setShowConfirmClear] = useState<{
    isOpen: boolean;
    data: PendingRemoval | null;
  }>({
    isOpen: false,
    data: null,
  });

  const allowRemove = item.allowRemove ?? true;
  const disableDecrease = item.disableDecrease ?? false;
  const disableIncrease = item.disableIncrease ?? false;
  const minValue = item.minValue ?? 1;
  const quantityInputId = `gift-card-quantity-${item.id}`;
  const availableQuantity =
    typeof item.usedQuantity === "number"
      ? Math.max(0, Number(value) - item.usedQuantity)
      : undefined;

  return (
    <div>
      <div className="mt-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md dark:border-gray-700 dark:bg-gray-800 sm:p-5">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 flex-1 items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-[#00A982] dark:bg-emerald-950/50 dark:text-emerald-300">
              <Gift className="h-5 w-5" aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                {item.title}
              </p>
              {item.description && (
                <span className="mt-1 inline-flex rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                  {item.description}
                </span>
              )}
            </div>
          </div>

          {typeof item.usedQuantity === "number" && (
            <div className="grid grid-cols-2 gap-2 sm:flex sm:min-w-[260px]">
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-900/40 sm:flex-1">
                <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  {t("eGiftCard.used_quantity")}
                </p>
                <p className="mt-0.5 text-base font-semibold text-gray-800 dark:text-gray-100">
                  {item.usedQuantity}
                </p>
              </div>
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 dark:border-emerald-900 dark:bg-emerald-950/40 sm:flex-1">
                <p className="text-[11px] font-medium uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
                  {t("eGiftCard.available_quantity")}
                </p>
                <p className="mt-0.5 text-base font-semibold text-emerald-800 dark:text-emerald-200">
                  {availableQuantity}
                </p>
              </div>
            </div>
          )}

          <div className="flex items-end justify-between gap-3 lg:justify-end">
            <div>
              <label
                htmlFor={quantityInputId}
                className="mb-1.5 block text-xs font-medium text-gray-600 dark:text-gray-300"
              >
                {t("createEGiftCard.totalQuantity")}
              </label>
              <div className="flex items-center rounded-lg border border-gray-200 bg-gray-50 p-1 dark:border-gray-600 dark:bg-gray-900/40">
                <Button
                  variant="ghost"
                  className="h-9 w-9 rounded-md p-0 text-[#00A982] hover:bg-emerald-100 hover:text-[#008F70] disabled:text-gray-300 dark:text-emerald-300 dark:hover:bg-emerald-950"
                  onClick={() => {
                    if (Number(value) - 1 < minValue) {
                      if (allowRemove) {
                        setShowConfirmClear({ isOpen: true, data: { id: item.id } });
                      }
                      return;
                    }
                    setValue(Number(value) - 1);
                    handleChangeValue(item.id, Number(value) - 1);
                  }}
                  disabled={disableDecrease}
                  type="button"
                  aria-label={t("createEGiftCard.decreaseQuantity")}
                  title={t("createEGiftCard.decreaseQuantity")}
                >
                  <Minus className="h-4 w-4" aria-hidden="true" />
                </Button>
                <Input
                  id={quantityInputId}
                  className="h-9 !w-16 border-0 bg-transparent px-1 text-center text-base font-semibold text-gray-900 shadow-none no-spinner focus-visible:ring-1 focus-visible:ring-[#00C49A] dark:bg-transparent dark:text-white"
                  type="number"
                  min={minValue}
                  value={value}
                  aria-label={t("createEGiftCard.totalQuantity")}
                  onChange={(event) => {
                    const nextValue = Math.max(minValue, Number(event.target.value));
                    setValue(nextValue);
                    handleChangeValue(item.id, nextValue);
                  }}
                />
                <Button
                  variant="ghost"
                  className="h-9 w-9 rounded-md p-0 text-[#00A982] hover:bg-emerald-100 hover:text-[#008F70] disabled:text-gray-300 dark:text-emerald-300 dark:hover:bg-emerald-950"
                  onClick={() => {
                    setValue(Number(value) + 1);
                    handleChangeValue(item.id, Number(value) + 1);
                  }}
                  disabled={disableIncrease}
                  type="button"
                  aria-label={t("createEGiftCard.increaseQuantity")}
                  title={t("createEGiftCard.increaseQuantity")}
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                </Button>
              </div>
            </div>

            {allowRemove && (
              <Button
                variant="ghost"
                type="button"
                className="mb-1 h-9 w-9 rounded-lg p-0 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40 dark:hover:text-red-300"
                onClick={() => handleRemoveValue(item.id)}
                aria-label={t("createEGiftCard.removeGiftCardValue")}
                title={t("createEGiftCard.removeGiftCardValue")}
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </Button>
            )}
          </div>
        </div>
      </div>

      <ConfirmModal
        open={showConfirmClear.isOpen ?? false}
        title=""
        message={t("createEGiftCard.removeGiftCardValueConfirmation")}
        data={showConfirmClear.data}
        confirmText={t("common.yes")}
        cancelText={t("common.no")}
        onConfirm={() => {
          setValue(Number(value) - 1);
          handleChangeValue(item.id, Number(value) - 1);
          setShowConfirmClear({ isOpen: false, data: null });
        }}
        onCancel={() => setShowConfirmClear({ isOpen: false, data: null })}
      />
    </div>
  );
}

export default EGiftCardQuantityEditor;
